import React, { useEffect, useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import LocalTaxiRoundedIcon from "@mui/icons-material/LocalTaxiRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import AccessibleRoundedIcon from "@mui/icons-material/AccessibleRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { calcEst, showEst, optionsRequired, FONT_FAMILY } from "../common/sharedFunctions";

const Section = ({ role }) => {
  const settings = useSelector((state) => state.settingsdata.settings);
  const cartypes = useSelector((state) => state.cartypes);
  const auth = useSelector((state) => state.auth);
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  useEffect(() => setData(cartypes.cars ? [...cartypes.cars].sort((a, b) => a.pos - b.pos) : []), [cartypes.cars]);

  const openBooking = (item) => {
    if (!(auth.profile && auth.profile.uid)) return navigate("/login");
    navigate(role === "driver" || (calcEst && !showEst && !optionsRequired && (role === "admin" || role === "fleetadmin")) ? "/bookings" : "/addbookings", { state: { carData: item } });
  };

  const services = [
    [<LocalTaxiRoundedIcon />, t("landing_service_demand_title"), t("landing_service_demand_text")],
    [<ScheduleRoundedIcon />, t("landing_service_schedule_title"), t("landing_service_schedule_text")],
    [<AccessibleRoundedIcon />, t("landing_service_accessible_title"), t("landing_service_accessible_text")],
    [<PaymentsRoundedIcon />, t("landing_service_payments_title"), t("landing_service_payments_text")],
    [<LocationOnRoundedIcon />, t("landing_service_tracking_title"), t("landing_service_tracking_text")],
    [<VerifiedUserRoundedIcon />, t("landing_service_local_title"), t("landing_service_local_text")],
  ];

  return (
    <>
      <Box component="section" sx={{ bgcolor: "#F6F8FB", px: { xs: 2.5, md: 6 }, py: { xs: 8, md: 12 } }}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          <Typography sx={eyebrow}>{t("landing_ride_way")}</Typography>
          <Typography component="h2" sx={heading}>{t("landing_choose_trip")}</Typography>
          <Typography sx={{ mt: 1.5, mb: 5, color: "#667085", fontFamily: FONT_FAMILY, fontSize: 17 }}>{t("landing_clear_choices")}</Typography>
          {data.length ? <Grid container spacing={3} alignItems="stretch">
            {data.map((item, key) => (
              <Grid item xs={12} sm={6} md={4} key={item.id || key} sx={{ display: "flex" }}>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", bgcolor: "white", border: "1px solid #E5EAF1", borderRadius: 5, overflow: "hidden", boxShadow: "0 12px 35px rgba(12,31,67,.06)", transition: "transform .2s, box-shadow .2s", "&:hover": { transform: "translateY(-5px)", boxShadow: "0 20px 45px rgba(12,31,67,.12)" } }}>
                  <Box sx={{ position: "relative", display: "grid", placeItems: "center", minHeight: 190, bgcolor: key === 1 ? "#FFF4BF" : "#EDF2FA", p: 2.5 }}>
                    {key === 0 && <Box sx={{ position: "absolute", top: 16, left: 16, bgcolor: "#0B1739", color: "white", borderRadius: 99, px: 1.3, py: .55, fontFamily: FONT_FAMILY, fontSize: 11, fontWeight: 800 }}>{t("landing_popular")}</Box>}
                    <Box component="img" alt={item.name} src={item.image} sx={{ width: "85%", maxWidth: 230, height: 130, objectFit: "contain" }} />
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", flex: 1, p: 3 }}>
                    <Typography sx={{ color: "#101828", fontFamily: FONT_FAMILY, fontSize: 23, fontWeight: 800 }}>{item.name}</Typography>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: .5, mt: 1.5 }}>
                      <Typography sx={{ color: "#0B1739", fontFamily: FONT_FAMILY, fontSize: 30, fontWeight: 850 }}>{settings.swipe_symbol === false ? `${settings.symbol}${item.rate_per_unit_distance}` : `${item.rate_per_unit_distance}${settings.symbol}`}</Typography>
                      <Typography sx={{ color: "#667085", fontFamily: FONT_FAMILY, fontSize: 14 }}>/ {settings.convert_to_mile ? t("mile") : t("km")}</Typography>
                    </Box>
                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #EDF0F4" }}>
                      <Typography sx={{ color: "#475467", fontFamily: FONT_FAMILY, fontSize: 14 }}>{t("min_fare")}: <b>{item.min_fare}</b></Typography>
                      {item.extra_info && <Typography sx={{ mt: .8, color: "#667085", fontFamily: FONT_FAMILY, fontSize: 13.5, lineHeight: 1.45, overflowWrap: "anywhere" }}>{item.extra_info}</Typography>}
                    </Box>
                    <Button onClick={() => openBooking(item)} variant="contained" fullWidth sx={{ mt: "auto", position: "relative", top: 18, mb: 2.2, py: 1.4, bgcolor: "#3867E8", borderRadius: 3, fontFamily: FONT_FAMILY, fontWeight: 800, textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: "#244FC8", boxShadow: "none" } }}>{t("book_now")}</Button>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid> : <Typography sx={{ py: 6, textAlign: "center", color: "#667085", fontFamily: FONT_FAMILY }}>{t("service_start_soon")}</Typography>}
        </Box>
      </Box>
      <Box component="section" sx={{ bgcolor: "white", px: { xs: 2.5, md: 6 }, py: { xs: 8, md: 12 } }}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          <Box sx={{ maxWidth: 650, mb: 5 }}><Typography sx={eyebrow}>{t("landing_built_day")}</Typography><Typography component="h2" sx={heading}>{t("landing_everything_needed")}</Typography></Box>
          <Grid container spacing={2.5}>
            {services.map(([icon, title, text]) => <Grid item xs={12} sm={6} md={4} key={title}>
              <Box sx={{ display: "flex", gap: 2, height: "100%", p: 2.5, border: "1px solid #E8ECF2", borderRadius: 4 }}>
                <Box sx={{ width: 46, height: 46, flex: "0 0 auto", display: "grid", placeItems: "center", bgcolor: "#FFF4BF", color: "#0B1739", borderRadius: 3 }}>{icon}</Box>
                <Box><Typography sx={{ color: "#101828", fontFamily: FONT_FAMILY, fontWeight: 800, fontSize: 17 }}>{title}</Typography><Typography sx={{ mt: .7, color: "#667085", fontFamily: FONT_FAMILY, fontSize: 14, lineHeight: 1.5 }}>{text}</Typography></Box>
              </Box>
            </Grid>)}
          </Grid>
        </Box>
      </Box>
    </>
  );
};

const eyebrow = { color: "#3867E8", fontFamily: FONT_FAMILY, fontWeight: 800, fontSize: 13, letterSpacing: 1.2, textTransform: "uppercase" };
const heading = { mt: 1, color: "#0B1739", fontFamily: FONT_FAMILY, fontWeight: 800, fontSize: { xs: 34, md: 48 }, letterSpacing: "-.04em", lineHeight: 1.08 };
export default Section;
